// Main Sidebar component
// Created as part of ENG2-1699: Create AppLayout Component with Sidebar

import React, { useMemo, useCallback } from 'react'
import { Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import {
  faBuilding,
  faGlobeEurope,
  faFileInvoice,
  faUsers,
  faBullhorn,
  faChartLine,
  faChartPie,
} from '@fortawesome/free-solid-svg-icons'
import { auth } from '../../firebase-config'
import { tokens, darkModeColors } from '../../styles/tokens'
import SidebarHeader from './sidebar-header'
import SidebarNav from './sidebar-nav'
import SidebarFooter from './sidebar-footer'
import { useSidebar } from './sidebar-context'
import { NavSection, SidebarUser, SidebarProps } from './sidebar.types'

// E2E test mode detection - checks URL param and localStorage
const checkE2ETestMode = (): boolean => {
  if (typeof window === 'undefined') return false

  // Check URL query parameter first
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('e2e') === 'true') {
    return true
  }

  // Check localStorage (for internal navigation)
  if (localStorage.getItem('E2E_TEST_MODE') === 'true') {
    return true
  }

  return false
}

const Sidebar: React.FC<SidebarProps> = ({ customerCount }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isCollapsed } = useSidebar()

  // Get current user from Firebase
  const currentUser = auth.currentUser

  // Check E2E mode at render time
  const isE2ETestMode = checkE2ETestMode()

  // Create user object for footer
  const sidebarUser: SidebarUser | null = useMemo(() => {
    // In E2E test mode, create mock user from localStorage
    if (isE2ETestMode) {
      const name = localStorage.getItem('FB_NAME') || 'Test User'
      const email = localStorage.getItem('FB_EMAIL') || 'test@risika.com'
      const nameParts = name.split(' ')
      const initials =
        nameParts.length >= 2
          ? `${nameParts[0][0]}${
              nameParts[nameParts.length - 1][0]
            }`.toUpperCase()
          : name.substring(0, 2).toUpperCase()
      return { name, email, initials }
    }

    if (!currentUser) return null

    const displayName = currentUser.displayName || currentUser.email || 'User'
    const email = currentUser.email || ''

    // Generate initials from display name or email
    const nameParts = displayName.split(' ')
    const initials =
      nameParts.length >= 2
        ? `${nameParts[0][0]}${
            nameParts[nameParts.length - 1][0]
          }`.toUpperCase()
        : displayName.substring(0, 2).toUpperCase()

    return {
      name: displayName,
      email,
      initials,
    }
  }, [currentUser, isE2ETestMode])

  // Navigation configuration
  const navigationSections: NavSection[] = useMemo(
    () => [
      {
        section: 'Main',
        items: [
          {
            label: 'Customers',
            icon: faBuilding,
            path: '/',
            badge: customerCount,
          },
          {
            label: 'Zones',
            icon: faGlobeEurope,
            path: '/zones',
          },
          {
            label: 'Global data usage',
            icon: faChartPie,
            path: '/global-data-usage',
          },
          {
            label: 'Nordic lookups usage',
            icon: faChartLine,
            path: '/nordic-lookups-usage',
          },
          {
            label: 'Banners',
            icon: faBullhorn,
            path: '/banners',
          },
          {
            label: 'Customer groups',
            icon: faUsers,
            path: '/customer-groups',
          },
          {
            label: 'Escalations',
            icon: faFileInvoice,
            path: '/escalations',
          },
        ],
      },
    ],
    [customerCount],
  )

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth)
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }, [navigate])

  // Determine current path for active state
  const currentPath = location.pathname

  return (
    <Box
      component="aside"
      sx={{
        width: isCollapsed
          ? tokens.layout.sidebarCollapsedWidth
          : tokens.layout.sidebarWidth,
        backgroundColor: darkModeColors.background.sidebar,
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 100,
        transition: `width ${tokens.transitions.normal}`,
        overflow: 'hidden',
      }}
    >
      <SidebarHeader isCollapsed={isCollapsed} />
      <SidebarNav sections={navigationSections} currentPath={currentPath} />
      <SidebarFooter user={sidebarUser} onLogout={handleLogout} />
    </Box>
  )
}

export default Sidebar
